import React, { useId } from 'react';
import { CircleAlert } from 'lucide-react';
import { cx } from './classNames';
import { spanClass, type FormSpan } from './FormGrid';
import './ui.css';

/** cloneElement で子に注入する属性。子は input / select / textarea 相当を想定 */
interface ControlProps {
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
}

export interface FieldProps {
  label: React.ReactNode;
  required?: boolean;
  /** 入力の下に出す補足。error があるときは error が優先される */
  help?: React.ReactNode;
  error?: React.ReactNode;
  /** FormGrid 内での幅。省略時は full */
  span?: FormSpan;
  /** 子要素に付ける id。省略時は自動採番 */
  id?: string;
  className?: string;
  /**
   * 単一のコントロール要素（<input> / <select> / <textarea> / Input 等）。
   * id・aria-describedby・aria-invalid・className="ui-control" を注入する。
   */
  children: React.ReactElement<ControlProps>;
}

export function Field({ label, required = false, help, error, span, id: idProp, className, children }: FieldProps) {
  const autoId = useId();
  const id = idProp ?? children.props.id ?? `ui-field-${autoId}`;
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const invalid = error != null && error !== '';

  const describedBy = cx(children.props['aria-describedby'], invalid ? errorId : help != null ? helpId : undefined);

  const injected: ControlProps = {
    id,
    className: cx('ui-control', children.props.className),
    'aria-describedby': describedBy || undefined,
    'aria-invalid': invalid || undefined,
    'aria-required': required || undefined,
  };

  return (
    <div className={cx('ui-field', spanClass(span ?? 'full'), className)}>
      <label className="ui-field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="ui-field__required" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {React.cloneElement(children, injected)}
      {invalid ? (
        <div className="ui-field__error" id={errorId} role="alert">
          <CircleAlert aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : (
        help != null && (
          <div className="ui-field__help" id={helpId}>
            {help}
          </div>
        )
      )}
    </div>
  );
}

/* Field の外で単独に使うときのための、クラスだけ付けた素の要素 */

export type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx('ui-control', className)} {...rest} />;
});

export type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(function Select({ className, ...rest }, ref) {
  return <select ref={ref} className={cx('ui-control', className)} {...rest} />;
});

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cx('ui-control', className)} {...rest} />;
});
